import { send } from '../../api/modules/apiV2';
import type { INotificationService, NotificationListItem } from './INotificationService';

/**
 * Host notification HTTP client. Uses the shared `send` helper so auth headers,
 * correlation headers, and the base URL match every other host API call.
 */
export class NotificationService implements INotificationService {
  async getUnread(): Promise<{ hasUnread: boolean; unreadCount: number }> {
    const result = await send('get', 'realtime/notifications/unread');
    this.assertOk(result, 'unread');
    const unreadCount = typeof result?.unreadCount === 'number' ? result.unreadCount : 0;
    return { hasUnread: !!result?.hasUnread || unreadCount > 0, unreadCount };
  }

  async getPage(cursor?: string): Promise<{ items: NotificationListItem[]; nextCursor: string | null }> {
    const url = cursor
      ? `realtime/notifications?cursor=${encodeURIComponent(cursor)}`
      : 'realtime/notifications';
    const result = await send('get', url);
    this.assertOk(result, 'page');
    return {
      items: result?.items ?? [],
      nextCursor: result?.nextCursor ?? null,
    };
  }

  async markRead(notificationId: string): Promise<{ readAt: string }> {
    const result = await send(
      'post',
      `realtime/notifications/${encodeURIComponent(notificationId)}/read`,
      {},
    );
    this.assertOk(result, 'mark-read');
    return { readAt: result?.readAt ?? '' };
  }

  /** `send` returns `{ success: false }` on HTTP failure instead of throwing. */
  private assertOk(result: unknown, label: string): void {
    if (!result || typeof result !== 'object' || !('success' in result)) return;
    const failed = result as { success: unknown; error?: unknown };
    if (failed.success !== false) return;
    const message =
      typeof failed.error === 'string' ? failed.error : `Notification ${label} request failed`;
    throw new Error(message);
  }
}
